import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {QlService} from 'features/core/shared/ql/service/ql.service';
import {
    CP_CONDITIONS_OF_SET_QUERY_NAME,
    CpProvisionAssignmentConditionService
} from '../../../../../service/ql/cp-provision-assignment-condition.service';
import {ProvisionFormerContractRequirement} from '../../../../../enum/provision-former-contract-requirement.enum';
import {
    CP_UNCONDITIONAL_LABEL,
    cpConditionModeIcon,
    cpConditionModeLabel,
    cpConditionModeOf,
    cpConditionModeSeverity
} from '../../../../../service/domain/cp-provision-assignment-columns';
import {CpConditionPreviewRow} from '../../../../../interface/cp-condition-preview-row.interface';

/**
 * Read-only preview of the condition set of ONE selected assignment row (K-05, K-06, K-07, K-10,
 * K-17, K-18), rendered below the assignment table by `CpProvisionTypeChildTableComponent`.
 *
 * It answers the question the assignment table can no longer answer by itself since the 13
 * contract dimensions moved into `cp.ProvisionAssignmentCondition`: "for which contracts does this
 * row apply?". Editing happens in the navItem "Bedingungssätze" — the preview offers the jump there
 * ({@link onJumpToConditionSet}) and deliberately has no edit affordance of its own, because a set
 * is SHARED between assignments and an edit here would silently change the other users too.
 *
 * Semantics stated in the component (backend matching): the conditions are checked in
 * `orderPriority` order, the FIRST match decides (INCLUDE = applies, EXCLUDE = does not apply); no
 * match = does not apply; a set without conditions never applies; no set at all = applies to every
 * contract.
 *
 * Data: one QL view query (`buildConditionsOfSetQuery`, dimension names joined) per selected set —
 * configuration data, a handful of rows. Loading / empty / error states are explicit.
 */
@Component({
    selector: 'cp-provision-assignment-condition-preview',
    templateUrl: './cp-provision-assignment-condition-preview.component.html',
    styleUrls: ['./cp-provision-assignment-condition-preview.component.scss'],
    standalone: false
})
export class CpProvisionAssignmentConditionPreviewComponent implements OnInit, OnChanges, OnDestroy {

    readonly unconditionalLabel = CP_UNCONDITIONAL_LABEL;

    busy: boolean;
    initialized: boolean;
    loadFailed: boolean;

    @Input() provisionTypeId: number;

    /** Name of the selected assignment — header text only. */
    @Input() assignmentName: string;

    /** Condition set of the selected assignment; `null` = unconditional assignment. */
    @Input() conditionSetId: number | null;
    @Input() conditionSetName: string | null;
    @Input() conditionSetAlias: string | null;

    /** "Im Bedingungssatz öffnen" — emits the set id. */
    @Output() onJumpToConditionSet = new EventEmitter<number>();

    /** Close button of the preview card. */
    @Output() onClose = new EventEmitter<void>();

    rows: CpConditionPreviewRow[] = [];

    private loadSubscription: Subscription;

    constructor(private qlService: QlService,
                private conditionService: CpProvisionAssignmentConditionService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.refreshComponent();
    }

    refreshComponent(): void {

        this.cancelLoad();
        this.rows = [];
        this.loadFailed = false;

        if (!this.conditionSetId) {
            this.busy = false;
            this.initialized = true;
            return;
        }

        this.busy = true;
        this.initialized = true;

        this.loadSubscription = this.qlService.builder()
            .showErrors(false)
            .query(this.conditionService.buildConditionsOfSetQuery(this.conditionSetId))
            .execute()
            .subscribe({
                next: result => {
                    this.rows = (result.get(CP_CONDITIONS_OF_SET_QUERY_NAME) ?? [])
                        .map(row => this.toPreviewRow(row));
                    this.busy = false;
                },
                error: () => {
                    this.rows = [];
                    this.loadFailed = true;
                    this.busy = false;
                }
            });
    }

    /** Retry after a failed load. */
    onRetry(): void {
        this.refreshComponent();
    }

    onJump(): void {
        if (this.conditionSetId) {
            this.onJumpToConditionSet.emit(this.conditionSetId);
        }
    }

    // --- formatting ------------------------------------------------------------------------- //

    private toPreviewRow(row: any): CpConditionPreviewRow {
        const mode = cpConditionModeOf(row?.['e.conditionMode']);
        return {
            id: +row?.['e.id'],
            orderPriority: row?.['e.orderPriority'],
            mode: mode,
            modeLabel: cpConditionModeLabel(mode),
            modeSeverity: cpConditionModeSeverity(mode),
            modeIcon: cpConditionModeIcon(mode),
            criteria: this.criteriaOf(row),
            description: row?.['e.description'] || null
        };
    }

    /**
     * The restricting dimensions of one condition as short German phrases. A NULL dimension is a
     * wildcard and is left out; `formerContractRequirement = ANY` is the wildcard of dimension 11.
     */
    private criteriaOf(row: any): string[] {

        const criteria: string[] = [];
        const named: [string, string][] = [
            ['g.name', 'Sparte'],
            ['prod.name', 'Produkt'],
            ['pa.name', 'Gesellschaft'],
            ['cct.name', 'Abschlussweg'],
            ['cot.name', 'Herkunft'],
            ['cbc.name', 'Basiskategorie'],
            ['cbt.name', 'Basisart'],
            ['cat.name', 'Kategorie'],
            ['ct.name', 'Vertragsart']
        ];

        for (const [key, label] of named) {
            const value = row?.[key];
            if (this.isFilled(value)) {
                criteria.push(label + ' = ' + value);
            }
        }

        const term = this.band(row?.['e.termMonthsFrom'], row?.['e.termMonthsTo']);
        if (term) {
            criteria.push('Laufzeit ' + term);
        }

        const former = this.formerRequirementLabel(row?.['e.formerContractRequirement']);
        if (former) {
            criteria.push(former);
        }

        if (this.isFilled(row?.['fpa.name'])) {
            criteria.push('Gesellschaft Vorvertrag = ' + row['fpa.name']);
        }

        const formerTerm = this.band(row?.['e.formerTermMonthsFrom'], row?.['e.formerTermMonthsTo']);
        if (formerTerm) {
            criteria.push('Laufzeit Vorvertrag ' + formerTerm);
        }

        return criteria;
    }

    /** "12–60 Monate", "ab 12 Monaten", "bis 60 Monate" or `null` without any bound. */
    private band(from: any, to: any): string | null {
        const hasFrom = this.isFilled(from);
        const hasTo = this.isFilled(to);
        if (hasFrom && hasTo) {
            return from + '–' + to + ' Monate';
        }
        if (hasFrom) {
            return 'ab ' + from + ' Monaten';
        }
        if (hasTo) {
            return 'bis ' + to + ' Monate';
        }
        return null;
    }

    /** Dimension 11; ANY (the wildcard) yields `null`. Ordinal or constant name accepted. */
    private formerRequirementLabel(value: any): string | null {
        if (!this.isFilled(value)) {
            return null;
        }
        const normalized = typeof value === 'string' ? value.trim().toUpperCase() : value;
        if (normalized === ProvisionFormerContractRequirement.WITH || normalized === 'WITH'
            || normalized === String(ProvisionFormerContractRequirement.WITH)) {
            return 'Mit Vorvertrag';
        }
        if (normalized === ProvisionFormerContractRequirement.WITHOUT || normalized === 'WITHOUT'
            || normalized === String(ProvisionFormerContractRequirement.WITHOUT)) {
            return 'Ohne Vorvertrag';
        }
        return null;
    }

    private isFilled(value: any): boolean {
        return value !== null && value !== undefined && value !== '';
    }

    private cancelLoad(): void {
        if (this.loadSubscription) {
            this.loadSubscription.unsubscribe();
            this.loadSubscription = null;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['conditionSetId'] || changes['provisionTypeId']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.cancelLoad();
    }

}
